import { useMemo } from 'react'
import { Target } from 'lucide-react'
import { CATEGORY_STYLES } from '../../../constants/mindmapConfig'
import { BackToMindmapCTA } from '../BackToMindmapCTA'
import {
  aggregateValidatedNodes,
  groupNodesByCategory,
  type QuestionWithSourceNodes,
} from './aggregateValidatedNodes'
import {
  IntroBlock,
  IntroTitle,
  IntroSubtitle,
  CategoryGroup,
  CategoryHeader,
  CategoryDot,
  CategoryTitle,
  NodeList,
  NodeRow,
  NodeLabel,
  QuestionCountBadge,
  EmptyCard,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
} from './ValidationScopeTabStyles'

export interface ValidationScopeTabProps {
  questions: QuestionWithSourceNodes[]
  mindmapId: string
  /** "survey" | "interview" — used in the intro copy. */
  validationKind: 'survey' | 'interview'
}

const CATEGORY_LABELS: Record<string, string> = {
  need: 'Needs',
  idea: 'Ideas',
  venture: 'Ventures',
  other: 'Other',
}

function colorForCategory(category: string): string {
  const style = CATEGORY_STYLES[category as keyof typeof CATEGORY_STYLES] as
    | { iconColor: string }
    | undefined
  return style?.iconColor ?? 'var(--color-text-secondary)'
}

export function ValidationScopeTab({
  questions,
  mindmapId,
  validationKind,
}: ValidationScopeTabProps) {
  const nodes = useMemo(() => aggregateValidatedNodes(questions), [questions])
  const groups = useMemo(() => groupNodesByCategory(nodes), [nodes])

  const kindNoun = validationKind === 'survey' ? 'survey' : 'interview'

  if (nodes.length === 0) {
    return (
      <EmptyCard>
        <EmptyIcon>
          <Target size={22} />
        </EmptyIcon>
        <EmptyTitle>Not linked to any nodes</EmptyTitle>
        <EmptyText>
          This {kindNoun} wasn't built from a branch of your mindmap, so it doesn't update any
          specific nodes. You'll still get AI findings from the responses.
        </EmptyText>
      </EmptyCard>
    )
  }

  const total = nodes.length

  return (
    <>
      <IntroBlock>
        <IntroTitle>
          This {kindNoun} covers {total} node{total !== 1 ? 's' : ''} on your mindmap
        </IntroTitle>
        <IntroSubtitle>
          Each response strengthens these nodes. Their confidence scores update once the findings
          are generated.
        </IntroSubtitle>
      </IntroBlock>

      {groups.map(({ category, nodes }) => {
        const color = colorForCategory(category)
        return (
          <CategoryGroup key={category}>
            <CategoryHeader>
              <CategoryDot $color={color} />
              <CategoryTitle>
                {CATEGORY_LABELS[category] ?? category} · {nodes.length}
              </CategoryTitle>
            </CategoryHeader>
            <NodeList>
              {nodes.map((node) => (
                <NodeRow key={node.id} $color={color}>
                  <NodeLabel title={node.label}>{node.label}</NodeLabel>
                  <QuestionCountBadge>
                    {node.questionCount} question{node.questionCount !== 1 ? 's' : ''}
                  </QuestionCountBadge>
                </NodeRow>
              ))}
            </NodeList>
          </CategoryGroup>
        )
      })}

      <BackToMindmapCTA
        mindmapId={mindmapId}
        title="Open the mindmap to see these nodes"
        description="Jump back to your mindmap to explore the nodes this validation is targeting."
        buttonLabel="Back to mindmap"
      />
    </>
  )
}
